import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { UserTrainingActivityDetailsModalComponent } from '../user-training-activity-details-modal/user-training-activity-details-modal.component';
import { UserActivityService } from '@app/user-activity/user-activity.service';
import { NotificationsService } from '@shared/notifications.service';
import { InteractionService } from '@app/interaction/interaction.service';
import { ConfirmStatus } from '@app/interaction/confirm/confirm.component';
import { TrainingDetails, ProgramDetails } from '@app/user-activity/types';

@Component({
    selector: 'app-user-activity',
    templateUrl: './user-activity.component.html',
    styleUrls: ['./user-activity.component.scss'],
    standalone: false
})

export class UserActivityComponent implements OnInit, OnChanges {
    @ViewChild('trainingActivityDetailsModal')
    trainingActivityDetailsModal: UserTrainingActivityDetailsModalComponent;

    @Input() userId?: number;
    @Input() activities?: any[]; // Array of training activities (can be empty)
    @Input() maxActivities: number = 3; // Number of entries to display
    @Input() enableCommenting: boolean = true; // Enable/disable commenting on activity
    
    displayedActivities: any[] = [];
    
    // Activity details modal state
    showActivityDetailsModal: boolean = false;
    isClosingActivityDetailsModal: boolean = false;
    activityDetails: TrainingDetails | ProgramDetails | null = null;
    activityDetailsType: 'training' | 'program' | null = null;
    isLoadingActivityDetails: boolean = false;
    selectedWeekIndex: number = 0;
    expandedDays: Set<string> = new Set();
    currentActivityForDetails: any = null;
    
    // Activity comment modal state
    showActivityCommentModal: boolean = false;
    editedActivityComment: string = '';
    isClosingActivityCommentModal: boolean = false;
    showActivityCommentError: boolean = false;
    isEditingActivityComment: boolean = false;
    editingActivityId: number | null = null;
    currentActivity: any = null;

    ngOnInit(): void {
        this.updateDisplayedActivities();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['activities'] || changes['maxActivities']) {
            this.updateDisplayedActivities();
        }
    }

    updateDisplayedActivities(): void {
        if (this.activities) {
            this.displayedActivities = [...this.activities].slice(0, this.maxActivities);
        }
    }

    openActivityDetailsModal(activity: any): void {
        if (activity.type === 'training') {
            this.trainingActivityDetailsModal.open(activity.id);
            return;
        }

        this.currentActivityForDetails = activity;
        this.isClosingActivityDetailsModal = false;
        this.showActivityDetailsModal = true;
        this.isLoadingActivityDetails = true;
        this.activityDetails = null;
        this.activityDetailsType = activity.type;
        this.selectedWeekIndex = 0;
        this.expandedDays.clear();

        if (activity.type === 'program') {
            this.userActivityService.getProgramDetails(activity.id).then((response) => {
                this.isLoadingActivityDetails = false;
                if (!response.error) {
                    this.activityDetails = response.data as ProgramDetails;
                } else {
                    this.notifications.error('Error', 'Failed to load activity details.');
                    this.closeActivityDetailsModal();
                }
            }).catch((error) => {
                this.isLoadingActivityDetails = false;
                this.notifications.error('Error', 'Failed to load activity details.');
                this.closeActivityDetailsModal();
            });
        }
    }

    closeActivityDetailsModal(): void {
        this.isClosingActivityDetailsModal = true;
        setTimeout(() => {
            this.showActivityDetailsModal = false;
            this.isClosingActivityDetailsModal = false;
            this.activityDetails = null;
            this.activityDetailsType = null;
            this.currentActivityForDetails = null;
            this.selectedWeekIndex = 0;
            this.expandedDays.clear();
        }, 180);
    }

    selectWeek(index: number): void {
        this.selectedWeekIndex = index;
    }

    toggleDayExpansion(weekIndex: number, dayIndex: number): void {
        const key = `${weekIndex}-${dayIndex}`;
        if (this.expandedDays.has(key)) {
            this.expandedDays.delete(key);
        } else {
            this.expandedDays.add(key);
        }
    }

    isDayExpanded(weekIndex: number, dayIndex: number): boolean {
        return this.expandedDays.has(`${weekIndex}-${dayIndex}`);
    }

    formatTime(seconds: number | undefined): string {
        if (!seconds) return '';
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    openAddActivityCommentModal(activity: any): void {
        if (!this.enableCommenting) return;
        
        this.editedActivityComment = '';
        this.isEditingActivityComment = false;
        this.editingActivityId = activity.id;
        this.currentActivity = activity;
        this.isClosingActivityCommentModal = false;
        this.showActivityCommentModal = true;
        this.showActivityCommentError = false;
    }

    openEditActivityCommentModal(activity: any): void {
        if (!this.enableCommenting) return;
        
        this.editedActivityComment = this.getLatestActivityNote(activity)?.content || '';
        this.isEditingActivityComment = true;
        this.editingActivityId = activity.id;
        this.currentActivity = activity;
        this.isClosingActivityCommentModal = false;
        this.showActivityCommentModal = true;
        this.showActivityCommentError = false;
    }

    closeActivityCommentModal(): void {
        this.isClosingActivityCommentModal = true;
        setTimeout(() => {
            this.showActivityCommentModal = false;
            this.isClosingActivityCommentModal = false;
            this.editedActivityComment = '';
            this.isEditingActivityComment = false;
            this.editingActivityId = null;
            this.currentActivity = null;
        }, 180);
    }

    confirmActivityComment(commentForm?: any): void {
        if (!this.enableCommenting) return;
        
        // Validate: comment content is required
        if (commentForm && commentForm.invalid) {
            this.showActivityCommentError = true;
            return;
        }

        if (!this.editedActivityComment?.trim()) {
            this.showActivityCommentError = true;
            return;
        }
        
        const activity = this.activities?.find(a => a.id === this.editingActivityId);
        if (!activity) return;
        
        this.showActivityCommentError = false;

        const latestNote = this.getLatestActivityNote(activity);
        const noteId = this.isEditingActivityComment ? latestNote?.id : undefined;

        this.userActivityService.createNote(this.editingActivityId!, activity.type, this.editedActivityComment, noteId).then((response) => {
            if (!response.error) {
                if (activity && activity.object) {
                    const existingNotes = Array.isArray(activity.object.notes) ? activity.object.notes : [];
                    const responseNote = response.data && typeof response.data === 'object' && 'id' in response.data
                        ? response.data
                        : null;

                    activity.object.notes = responseNote
                        ? [responseNote, ...existingNotes]
                        : [
                            {
                                id: 0,
                                content: this.editedActivityComment,
                                created_by: '',
                                created_at: new Date().toISOString()
                            },
                            ...existingNotes
                        ];
                }
                this.notifications.success('Success', this.isEditingActivityComment ? 'Comment updated successfully.' : 'Comment added successfully.');
                this.closeActivityCommentModal();
            }
        });
    }

    async deleteActivityComment(activity: any): Promise<void> {
        if (!this.enableCommenting) return;

        const isConfirmed = await this.interaction.confirm({
            title: 'Delete comment?',
            message: 'Are you sure you want to delete this comment? This action is permanent and cannot be undone.',
            buttons: [
                {
                    label: 'Cancel',
                    value: ConfirmStatus.CANCELED,
                    style: 'default'
                },
                {
                    label: 'Delete message',
                    value: ConfirmStatus.CONFIRMED,
                    style: 'danger'
                }
            ]
        });
        
        if (!isConfirmed) {
            return;
        }

        const activityItem = this.activities?.find(a => a.id === activity.id);
        if (!activityItem) return;

        const latestNote = this.getLatestActivityNote(activityItem);
        if (!latestNote?.id) {
            return;
        }

        this.userActivityService.deleteNote(latestNote.id).then((response) => {
            if (!response.error) {
                this.notifications.success('Success', 'Comment deleted successfully.');
                if (activityItem && activityItem.object) {
                    activityItem.object.notes = (activityItem.object.notes || []).filter((note: any) => note.id !== latestNote.id);
                }
            }
        });
    }

    hasActivityNotes(activity: any): boolean {
        return Array.isArray(activity?.object?.notes) && activity.object.notes.length > 0;
    }

    getLatestActivityNote(activity: any): any | null {
        if (!this.hasActivityNotes(activity)) {
            return null;
        }

        return activity.object.notes[0];
    }

    constructor(
        private userActivityService: UserActivityService,
        private notifications: NotificationsService,
        private interaction: InteractionService
    ) {}
}

